import { FieldProps, FormikTouched } from 'formik';
import { useCallback, useMemo } from 'react';
import {
  FormPageInputItemSingleSelection,
  FormPageInputItemSingleSelectionRadio,
  SingleSelectionProperty,
  FormPageInputData,
  SINGLE_SELECTION_PROPERTY_INPUT_TYPE,
  FormPageInputItemSingleSelectionPulldown,
} from 'shared';
import { useAssortedDrillDownProperties } from 'store';
import { useFieldPropsHandler } from '../useFieldPropsHandler';
import { SingleSelectionInputProps } from './shared';
import { useDrillDownFieldPropsHandler } from './useDrillDownFieldPropsHandler';

const VALUE_KEY: keyof Pick<FormPageInputItemSingleSelection, 'value'> =
  'value';

type InputItem = FormPageInputItemSingleSelection;

type UseSingleSelectionPayload = {
  fieldProps: FieldProps<FormPageInputItemSingleSelection, FormPageInputData>;
  property: SingleSelectionProperty;
};

type UseSingleSelectionReturnType = {
  fieldTouched: FormikTouched<InputItem> | undefined;
  fieldErrorMessages: string[] | undefined;
  handleSelectChange: SingleSelectionInputProps['onSelectChange'];
  handleSubtextBlur: SingleSelectionInputProps['onSubtextBlur'];
  handleSubtextChange: SingleSelectionInputProps['onSubtextChange'];
  parentValue: FormPageInputItemSingleSelectionPulldown['value'];
};

export const useSingleSelection = ({
  fieldProps,
  property,
}: UseSingleSelectionPayload): UseSingleSelectionReturnType => {
  const { propertyId, drillDown, inputFormat } = property;
  const assortedDrillDownProperties = useAssortedDrillDownProperties();

  const assortedDrillDownSettingPropertiesGroup = useMemo(() => {
    if (
      inputFormat.inputType !== SINGLE_SELECTION_PROPERTY_INPUT_TYPE.pulldown
    ) {
      return undefined;
    }
    return assortedDrillDownProperties.find((group) =>
      group.some((property) => property.propertyId === propertyId)
    );
  }, [assortedDrillDownProperties, inputFormat.inputType, propertyId]);

  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldValueWithCallback,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemSingleSelection>({
    fieldProps,
    propertyId,
  });

  const { parentValue, updateDrillDownFieldValue } =
    useDrillDownFieldPropsHandler({
      drillDownSetting: drillDown,
      fieldProps,
      propertyId,
      assortedDrillDownSettingPropertiesGroup,
    });

  const updateSubtextValue = useCallback(
    (updateValue: number | null, updateSubtext: string) => {
      updateFieldValueWithCallback((currentPropertyValues) => ({
        ...currentPropertyValues,
        subtexts: (
          currentPropertyValues as FormPageInputItemSingleSelectionRadio
        )?.subtexts?.map((subtext) => ({
          value: subtext.value,
          subtext:
            updateValue === subtext.value ? updateSubtext : subtext.subtext,
        })),
      }));
    },
    [updateFieldValueWithCallback]
  );

  const handleSelectChange: SingleSelectionInputProps['onSelectChange'] =
    useCallback(
      (updateValue) => {
        if (assortedDrillDownSettingPropertiesGroup) {
          updateDrillDownFieldValue(VALUE_KEY, updateValue);
        } else {
          updateFieldValue(VALUE_KEY, updateValue);
        }
        updateFieldTouched(VALUE_KEY);
      },
      [
        assortedDrillDownSettingPropertiesGroup,
        updateDrillDownFieldValue,
        updateFieldTouched,
        updateFieldValue,
      ]
    );
  const handleSubtextChange: SingleSelectionInputProps['onSubtextChange'] =
    useCallback(
      (updateValue, updateSubtext) => {
        updateSubtextValue(updateValue, updateSubtext);
      },
      [updateSubtextValue]
    );
  const handleSubtextBlur: SingleSelectionInputProps['onSubtextBlur'] =
    useCallback(
      (updateValue, updateSubtext) => {
        updateSubtextValue(updateValue, updateSubtext);
        updateFieldTouched(VALUE_KEY);
      },
      [updateFieldTouched, updateSubtextValue]
    );

  return {
    parentValue,
    fieldErrorMessages,
    fieldTouched,
    handleSelectChange,
    handleSubtextChange,
    handleSubtextBlur,
  };
};
